import { useState } from "react";
import { toast } from "sonner";
import axios from "axios";
import { Copy } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const InvitationsTab = ({ getAuthHeaders }) => {
  const [invName, setInvName] = useState("");
  const [invNickname, setInvNickname] = useState("");
  const [invEmail, setInvEmail] = useState("");
  const [invLoading, setInvLoading] = useState(false);
  const [invResult, setInvResult] = useState(null);
  const [countdown, setCountdown] = useState(0);

  const createInvitation = async (e) => {
    e.preventDefault();
    if (!invName.trim() || !invEmail.trim()) return;
    setInvLoading(true);
    try {
      const res = await axios.post(`${API}/member-codes/create-invitation`,
        { name: invName.trim(), nickname: invNickname.trim(), email: invEmail.trim() },
        { headers: getAuthHeaders() });
      setInvResult(res.data);
      setCountdown(120);
      toast.success(`Invitationskode oprettet til ${invName}`);
      setInvName(""); setInvNickname(""); setInvEmail("");
      // Start countdown
      const interval = setInterval(() => {
        setCountdown(c => {
          if (c <= 1) { clearInterval(interval); return 0; }
          return c - 1;
        });
      }, 1000);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Kunne ikke oprette invitation");
    } finally {
      setInvLoading(false);
    }
  };

  return (
    <div className="max-w-lg">
      <h2 className="text-2xl font-['Oswald'] font-bold text-white mb-4">OPRET INVITATION</h2>
      <p className="text-gray-400 mb-6">Opret en invitationskode til et nyt medlem. Koden er gyldig i 2 minutter.</p>

      {invResult && countdown > 0 && (
        <div className="mc-card p-6 border-green-500/30 mb-6" data-testid="invitation-result">
          <div className="text-center">
            <p className="text-gray-400 text-sm mb-2">Invitationskode til {invResult.name}:</p>
            <p className="text-4xl font-mono font-bold text-green-400 tracking-widest mb-3" data-testid="generated-code">{invResult.code}</p>
            <div className="flex items-center justify-center gap-3">
              <button onClick={() => { navigator.clipboard.writeText(invResult.code); toast.success("Kopieret!"); }}
                className="flex items-center gap-2 px-4 py-2 rounded-lg bg-green-600/20 text-green-400 hover:bg-green-600/30 transition-colors"
                data-testid="copy-code-btn">
                <Copy className="w-4 h-4" /> Kopier
              </button>
            </div>
            <div className="mt-4">
              <div className="w-full bg-neutral-800 rounded-full h-2">
                <div className="bg-green-500 h-2 rounded-full transition-all duration-1000" style={{ width: `${(countdown / 120) * 100}%` }} />
              </div>
              <p className={`text-sm mt-2 ${countdown < 30 ? 'text-red-400' : 'text-gray-400'}`}>
                {Math.floor(countdown / 60)}:{String(countdown % 60).padStart(2, '0')} tilbage
              </p>
            </div>
          </div>
        </div>
      )}

      <form onSubmit={createInvitation} className="mc-card p-6 space-y-4">
        <div>
          <label className="block text-sm text-gray-400 mb-2">Navn *</label>
          <input type="text" value={invName} onChange={(e) => setInvName(e.target.value)} className="mc-input w-full" placeholder="Fulde navn" required data-testid="inv-name-input" />
        </div>
        <div>
          <label className="block text-sm text-gray-400 mb-2">Klubnavn</label>
          <input type="text" value={invNickname} onChange={(e) => setInvNickname(e.target.value)} className="mc-input w-full" placeholder="Valgfrit" data-testid="inv-nickname-input" />
        </div>
        <div>
          <label className="block text-sm text-gray-400 mb-2">Email *</label>
          <input type="email" value={invEmail} onChange={(e) => setInvEmail(e.target.value)} className="mc-input w-full" placeholder="email@example.dk" required data-testid="inv-email-input" />
        </div>
        <button type="submit" disabled={invLoading || !invName.trim() || !invEmail.trim()}
          className="w-full py-3 rounded-xl font-['Oswald'] uppercase tracking-wider bg-green-600 hover:bg-green-500 text-white disabled:opacity-50 transition-colors"
          data-testid="create-invitation-submit">
          {invLoading ? <><span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin inline-block mr-2" />Opretter...</> : "Opret invitationskode"}
        </button>
      </form>
    </div>
  );
};

export default InvitationsTab;
